import { inject } from "../../di";
import type { IWritableNotificationsState, INotificationsService } from "./types";
import type { IRpcService } from "../rpc/types";
import type { IAuthState } from "../auth/types";
import type { GetNotificationsResult } from "api-contracts/rpc/notifications";

export class NotificationsService implements INotificationsService {
  @inject<IWritableNotificationsState>("writableNotificationsState")
  private state!: IWritableNotificationsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  private pendingLoad: Promise<void> | undefined;

  loadNotifications(): void {
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      await this.authState.ready;
      if (!this.authState.isAuthenticated.get()) {
        return;
      }

      const result = await this.rpcService.call<GetNotificationsResult>("get-notifications", {
        limit: "50",
      });

      this.state.notifications.set(result.notifications);
      this.state.unreadCount.set(parseInt(result.unreadCount));
      this.state.chatNotifications.set(result.chatNotifications);
    } catch (error) {
      console.error("[NotificationsService] Error loading notifications:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  async markAllRead(): Promise<void> {
    const currentNotifications = this.state.notifications.get();
    const hasUnread = currentNotifications.some((n) => !n.isRead);
    const hasChatUnread = this.state.chatNotifications.get().length > 0;
    if (!hasUnread && !hasChatUnread) return;

    try {
      await this.rpcService.call("mark-all-notifications-read", {});

      const updated = currentNotifications.map((n) => ({
        ...n,
        isRead: true,
        readAt: n.readAt ?? new Date().toISOString(),
      }));
      this.state.notifications.set(updated);
      this.state.chatNotifications.set([]);
      this.state.unreadCount.set(0);
    } catch (error) {
      console.error("[NotificationsService] Error marking notifications as read:", error);
    }
  }
}
